export type RatingPeriod = 'day' | 'week' | 'month' | 'quarter';

export interface CombinedPairRatingItem {
  symbol: string;
  strategyType: string | null;
  profitPercent: number | null;
  averageReturn: number | null;
  stdDeviation: number | null;
  volatility: number | null;
  maxDrawdown: number | null;
  sharpeRatio: number | null;
  stabilityScore: number | null;
  winRate: number | null;
  botsCount: number;
  tradesCount: number;
  stableRank: number | null;
  profitRank: number | null;
}

export interface PairRecommendationHighlights {
  mostStablePair: CombinedPairRatingItem | null;
  bestModerateRiskPair: CombinedPairRatingItem | null;
  mostReliableDrawdownPair: CombinedPairRatingItem | null;
  topProfitPair: CombinedPairRatingItem | null;
}

export interface PairRecommendationResponse {
  period: RatingPeriod;
  periodKey?: string;
  lastUpdated: string | null;
  sources: {
    stable: {
      periodStart: string;
      periodEnd: string;
      lastUpdated: string | null;
      totalItems: number;
    };
    profit: {
      periodStart: string;
      periodEnd: string;
      lastUpdated: string | null;
      totalItems: number;
    };
  };
  summary: {
    stablePairsCount: number;
    profitPairsCount: number;
    combinedPairsCount: number;
  };
  highlights: PairRecommendationHighlights;
  leaderboards: {
    stable: CombinedPairRatingItem[];
    profit: CombinedPairRatingItem[];
  };
}

export const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const formatPercent = (value: number | null | undefined, digits = 2): string =>
  isFiniteNumber(value) ? `${value.toFixed(digits)}%` : '-';

export const formatNumber = (value: number | null | undefined, digits = 2): string =>
  isFiniteNumber(value) ? value.toFixed(digits) : '-';

export const formatScore = (value: number | null | undefined, digits = 0): string =>
  isFiniteNumber(value) ? value.toFixed(digits) : '-';

export const getPairKey = (
  item: Pick<CombinedPairRatingItem, 'symbol' | 'strategyType'>
): string => `${String(item.symbol || '').trim().toUpperCase()}::${String(item.strategyType || '').trim()}`;
